import { useState } from 'react';
import { Plus, Trash2, RotateCw } from 'lucide-react';
import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';

type StiffSideMethod = 'ram-tester' | 'water-float' | 'seam-method' | 'spine-app' | 'skipped';

interface ArrowEntry {
  /** User-assigned number written on the arrow with sharpie. */
  number: number;
  /** Final indexed orientation, in 0/90/180/270 degrees of nock rotation from "stock." */
  indexedDegrees?: 0 | 90 | 180 | 270;
  /** Where the bareshaft landed BEFORE rotation. */
  initialResult?: 'bullseye' | 'left' | 'right' | 'high' | 'low';
  /** Notes per arrow (optional). */
  note?: string;
}

interface NockTuningData {
  stiffSideMethod?: StiffSideMethod;
  testDistanceYd?: number;
  arrows: ArrowEntry[];
  /** True once the user has run the test and committed indexed positions. */
  completed?: boolean;
}

interface Props { tuning: UseTuningSession }

/**
 * Step 10 — Nock Tuning (Indexing)
 *
 * Why it matters: even with a perfectly tuned bow, individual arrows in a
 * set can launch with slightly different dynamic-spine vectors because each
 * shaft has a stiff side and a weak side that may not be in the same
 * rotational position when you nock it up. Two arrows with identical specs
 * can clear the rest differently if their stiff sides are oriented
 * differently. At 20 yards you barely notice. At 60+ yards it becomes the
 * difference between a fist-size group and a baseball-size group.
 *
 * The fix: rotate each arrow's nock until it leaves the bow the same way.
 * Then mark THAT orientation as the "cock vane" position for that arrow,
 * and re-fletch (or just mark the cock vane) so every arrow goes through
 * the rest with its stiff side in the same place.
 *
 * Sources: Easton Archery dynamic-tuning whitepaper, John Dudley (Nock On)
 * "Nock Tuning Explained," Tim Gillingham nock-indexing demo, Levi Morgan
 * tuning sequence — all converge on this same method.
 */
export function NockTuningStep({ tuning }: Props) {
  const data = (tuning.session.stepData['nock_tuning'] as NockTuningData | undefined) ?? { arrows: [] };
  const update = (patch: Partial<NockTuningData>) =>
    tuning.updateStepData<NockTuningData>('nock_tuning', { ...data, ...patch });

  const [newArrowNum, setNewArrowNum] = useState<string>('');

  const addArrow = () => {
    const n = parseInt(newArrowNum, 10);
    if (!n || n < 1 || n > 99) return;
    if (data.arrows.some((a) => a.number === n)) return;
    update({ arrows: [...data.arrows, { number: n }].sort((a, b) => a.number - b.number) });
    setNewArrowNum('');
  };

  const updateArrow = (number: number, patch: Partial<ArrowEntry>) => {
    update({ arrows: data.arrows.map((a) => (a.number === number ? { ...a, ...patch } : a)) });
  };

  const removeArrow = (number: number) => {
    update({ arrows: data.arrows.filter((a) => a.number !== number) });
  };

  const allIndexed = data.arrows.length > 0 && data.arrows.every((a) => a.indexedDegrees !== undefined);

  return (
    <>
      <GoalCard title="What we're trying to achieve">
        Every arrow in your dozen leaves the bow flexing the SAME way. Not just one well-tuned
        arrow — twelve well-tuned arrows that group like one. This is the step most archers skip,
        and it's the #1 reason groups open up past 40 yards on an otherwise "tuned" bow.
      </GoalCard>

      {/* ─── The "why" — the dynamic-spine consistency story ─── */}
      <InfoCard title="Why nock tuning matters">
        Every carbon shaft has a stiff side and a weak side around its circumference. The seam
        from the manufacturing mandrel is the stiffest line on the arrow. When you nock the arrow
        and shoot, the shaft flexes around the rest — and if the stiff side is "up" on one arrow
        and "left" on another, they paradox differently coming off the rest. Two identical arrows,
        same spine number, same weight, will fly subtly different.
        <p className="mt-2">
          Nock tuning rotates the nock until each arrow's stiff side is oriented in the position
          where it leaves the bow cleanest, then you mark THAT orientation as the cock vane.
          After indexing, every arrow goes through the rest with its stiff side in the same place.
          Groups tighten at distance. Broadheads stop wandering. Bareshafts and fletched arrows
          impact together arrow-after-arrow, not just the lucky one.
        </p>
      </InfoCard>

      {/* ─── Step 1: Find the stiff side ─── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">Step 1 — Identify the stiff side (optional pre-work)</h3>
        <p className="text-[13px] text-zinc-400 mb-3 leading-relaxed">
          You can skip straight to rotation testing without this — but starting from the stiff side
          dramatically cuts the number of rotation tests per arrow. Pick whichever method you have
          tools for:
        </p>
        <div className="grid sm:grid-cols-2 gap-2 text-[13px]">
          {([
            { key: 'ram-tester', label: 'RAM / Beiter spine tester', detail: 'Spin the shaft on a digital spine tester, mark the position where deflection is lowest. That\'s the stiff side. Gold standard.' },
            { key: 'water-float', label: 'Water-float test', detail: 'Float a bareshaft in a sink of water. The heavy/seam side rolls down. Mark the side that ends up DOWN — that\'s the stiff side.' },
            { key: 'seam-method', label: 'Visual seam method', detail: 'On most premium shafts you can see the seam under bright light at a glancing angle. Mark the seam line — that\'s the stiff side.' },
            { key: 'spine-app', label: 'Phone-app spine apps', detail: 'Apps like Arrow Inspector use phone gyros to read shaft deflection. Reasonable for a budget setup.' },
            { key: 'skipped', label: 'Skip — just rotate and test', detail: 'Valid. Rotate 90° at a time during the shoot-test. Takes 2× as long but no special tools needed.' },
          ] as { key: StiffSideMethod; label: string; detail: string }[]).map((opt) => {
            const active = data.stiffSideMethod === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => update({ stiffSideMethod: opt.key })}
                className={`text-left p-3 rounded-md border transition ${
                  active
                    ? 'border-ember-500 bg-ember-500/10'
                    : 'border-zinc-700 bg-zinc-950 hover:border-zinc-600'
                }`}
              >
                <div className="font-semibold text-[13px]">{opt.label}</div>
                <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug">{opt.detail}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* ─── Step 2: The shoot-test procedure ─── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">Step 2 — Shoot-test each arrow</h3>
        <ol className="list-decimal list-inside space-y-2 text-[13px] text-zinc-300 leading-relaxed">
          <li>
            Number every arrow with a silver sharpie on the shaft (1, 2, 3...). You'll move the
            number to the cock vane later, but for now you need to track which arrow is which.
          </li>
          <li>
            <strong>Pull the fletching off</strong> a tested arrow OR use a dedicated bareshaft.
            Fletching masks dynamic-spine differences. You need the unfletched flight to read the
            arrow's natural launch tendency.
          </li>
          <li>
            Set up a target with a small aim dot at{' '}
            <input
              type="number"
              min={15}
              max={40}
              value={data.testDistanceYd ?? 25}
              onChange={(e) => update({ testDistanceYd: parseInt(e.target.value, 10) || undefined })}
              className="inline-block w-14 mx-1 bg-zinc-950 border border-zinc-700 rounded px-2 py-0.5 text-sm font-mono text-center"
            />
            {' '}yards. (25 yd is the sweet spot — far enough for spine to show, close enough to
            stay accurate.)
          </li>
          <li>
            Shoot arrow #1. Note where the bareshaft lands relative to the dot. If it hits the dot
            (or very close), this arrow's stock orientation is already its best position. Mark the
            current cock-vane location and move on.
          </li>
          <li>
            If it misses — rotate the nock <strong>90°</strong>. Shoot again. Repeat: 0° → 90° → 180°
            → 270°. Most arrows have an obvious "best" orientation where they hit closest to the dot.
          </li>
          <li>
            Mark that orientation on the shaft (a notch in the sharpie, or a dot at the cock-vane
            position). That's where the cock vane goes when you re-fletch.
          </li>
          <li>Repeat for every arrow in the set. Record results below.</li>
        </ol>
      </Card>

      <TipCard title="Pro tip — bareshaft + paper combo">
        Some archers prefer running this at a paper tuning rig instead of an outdoor target.
        Shoot the bareshaft through paper at 6 ft, note the tear direction, rotate the nock 90°,
        re-shoot, repeat until you get the cleanest bullet-hole. Faster than going to the range,
        works well in winter or for high-volume shops. Same result either way — the bareshaft
        tells you the truth about each arrow's launch.
      </TipCard>

      {/* ─── Step 3: Per-arrow tracker ─── */}
      <Card padding="lg">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Step 3 — Track your indexed positions</h3>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={99}
              placeholder="#"
              value={newArrowNum}
              onChange={(e) => setNewArrowNum(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addArrow()}
              className="w-14 bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-sm font-mono text-center"
            />
            <button
              onClick={addArrow}
              className="text-xs bg-ember-500/80 hover:bg-ember-500 text-zinc-950 font-semibold rounded px-3 py-1.5 flex items-center gap-1"
            >
              <Plus size={12} />
              Add arrow
            </button>
          </div>
        </div>

        {data.arrows.length === 0 ? (
          <p className="text-[12px] text-zinc-500 italic">
            Add each arrow as you test it (numbered 1-12 typically). The form will let you record
            initial impact direction and final indexed nock rotation.
          </p>
        ) : (
          <div className="space-y-2">
            {data.arrows.map((a) => (
              <div key={a.number} className="bg-zinc-950 border border-zinc-800 rounded-md p-3">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <span className="font-bold text-ember-500 font-mono">Arrow #{a.number}</span>
                  <button
                    onClick={() => removeArrow(a.number)}
                    className="text-zinc-500 hover:text-rose-400 p-1"
                    aria-label={`Remove arrow ${a.number}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[12px]">
                  <label className="block">
                    <span className="text-zinc-400 block mb-1">Initial impact (no rotation)</span>
                    <select
                      value={a.initialResult ?? ''}
                      onChange={(e) => updateArrow(a.number, { initialResult: e.target.value as ArrowEntry['initialResult'] || undefined })}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs"
                    >
                      <option value="">— select —</option>
                      <option value="bullseye">Bullseye (no rotation needed)</option>
                      <option value="left">Left of dot</option>
                      <option value="right">Right of dot</option>
                      <option value="high">High</option>
                      <option value="low">Low</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-zinc-400 block mb-1 flex items-center gap-1">
                      <RotateCw size={11} /> Final indexed rotation
                    </span>
                    <select
                      value={a.indexedDegrees ?? ''}
                      onChange={(e) => updateArrow(a.number, { indexedDegrees: e.target.value ? Number(e.target.value) as ArrowEntry['indexedDegrees'] : undefined })}
                      className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs"
                    >
                      <option value="">— select —</option>
                      <option value={0}>0° (stock orientation)</option>
                      <option value={90}>90° clockwise</option>
                      <option value={180}>180°</option>
                      <option value={270}>270° (90° counter-clockwise)</option>
                    </select>
                  </label>
                </div>
                <input
                  type="text"
                  placeholder="Notes (optional) — e.g. 'best of the set' or 'seam on right side'"
                  value={a.note ?? ''}
                  onChange={(e) => updateArrow(a.number, { note: e.target.value })}
                  className="w-full mt-2 bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-[11px] text-zinc-300"
                />
              </div>
            ))}
            {allIndexed && (
              <div className="text-[12px] text-emerald-400 font-medium pt-1">
                ✓ All {data.arrows.length} arrows indexed. Move the sharpie number to the cock vane
                of each, then refletch (or leave as-is if you used existing fletching) before the
                walk-back test.
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ─── Step 4: Lock it in ─── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">Step 4 — Lock it in before walk-back</h3>
        <ol className="list-decimal list-inside space-y-1.5 text-[13px] text-zinc-300 leading-relaxed">
          <li>
            <strong>Move the arrow number to the cock vane.</strong> Once you've found each arrow's
            indexed orientation, that orientation becomes its identity. Number the cock vane (or
            mark with a dot of paint), then erase the number off the shaft.
          </li>
          <li>
            <strong>If you're re-fletching:</strong> set the fletching jig so the cock vane lands at
            the indexed position you marked. The other two/three vanes follow at 120°/72° as normal.
          </li>
          <li>
            <strong>If you're keeping existing fletching:</strong> rotate the entire arrow in the
            quiver so it nocks up with the cock vane at the same clock position every time. The
            indexed position becomes "cock vane up" (or "cock vane out," whatever you shoot).
          </li>
          <li>
            Re-shoot a few groups at distance. Groups should be noticeably tighter than before
            indexing. If they're not, recheck your bow tune — the rest is right, the cam timing is
            right, your form is the variable.
          </li>
        </ol>
      </Card>

      <WarningCard title="When NOT to bother nock tuning">
        <ul className="list-disc list-inside space-y-1">
          <li>
            Brand-new shafts that haven't been through ~50 arrows of break-in. Carbon settles in the
            first hundred or so shots. Indexing too early just means you re-do it.
          </li>
          <li>
            High-end target arrows that come pre-sorted (Easton X10 Pro Match Grade, etc.) are already
            spine-aligned at the factory — the seam is intentionally placed in a consistent position
            across the dozen. Skip indexing or confirm with a spot-check.
          </li>
          <li>
            If your bow is fundamentally out of tune (cam timing off, tiller off, rest position
            wrong), nock tuning won't fix it. Fix the bow first.
          </li>
        </ul>
      </WarningCard>

      <InfoCard title="The takeaway">
        Nock tuning isn't magic — it's the difference between a bow that shoots ONE great group
        with the lucky arrow and a bow that shoots a tight group with EVERY arrow. Most archers
        chase their tail by replacing components or re-tuning the bow when the real problem is
        arrow-to-arrow inconsistency. 20 minutes with a sharpie and a bareshaft solves it.
      </InfoCard>
    </>
  );
}
